import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Building, Search, Plus, Check, AlertTriangle, Loader2, Users, X, Mail } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PantheonCompanySelector - Mandatory company selector from Pantheon list
 * 
 * Features:
 * - Search and select from existing Pantheon companies
 * - Add new company if not found (sends email to acc@europacific.com)
 * - Duplicate detection before adding
 * - Shows Pantheon sync status
 */
export const PantheonCompanySelector = ({ 
  value, 
  onChange, 
  placeholder = "Search Pantheon companies...",
  required = true,
  showGroupOption = false,
  className = ""
}) => {
  const { getAuthHeaders } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(value || null);
  const [showAddNewDialog, setShowAddNewDialog] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState({
    name: '',
    country: '',
    city: '',
    address: '',
    vat_number: '',
    email: '',
    contact_person: '',
    phone: ''
  });
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const searchCompanies = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCompanies([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/crm/companies/search-pantheon?q=${encodeURIComponent(query)}&limit=20`,
        { headers: getAuthHeaders() }
      );
      setCompanies(response.data.companies || []);
      
      // Also fetch groups if enabled
      if (showGroupOption) {
        const groupsResponse = await axios.get(
          `${API_URL}/api/customer-groups?search=${encodeURIComponent(query)}`,
          { headers: getAuthHeaders() }
        );
        setGroups(groupsResponse.data || []);
      }
    } catch (error) {
      console.error('Failed to search companies:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, showGroupOption]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchCompanies(searchQuery);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, searchCompanies]);

  // Sync selected value with parent
  useEffect(() => {
    if (value && !selectedCompany) {
      setSelectedCompany(value);
    }
  }, [value, selectedCompany]);

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    onChange(company);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    setSelectedCompany(null);
    onChange(null);
    setSearchQuery('');
  };

  const handleAddNewCompany = async () => {
    if (!newCompanyData.name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setAddingCompany(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/crm/companies/register-new-pantheon`,
        newCompanyData,
        { headers: getAuthHeaders() }
      );
      
      if (response.data.duplicate) {
        // Company already exists
        toast.warning(`Company "${response.data.existing.name}" already exists. Selecting it.`);
        handleSelectCompany(response.data.existing);
      } else {
        // New company added
        toast.success(
          <div>
            <p className="font-medium">Company registered successfully!</p>
            <p className="text-xs text-slate-500">Email sent to acc@europacific.com to add to Pantheon accounting system.</p>
          </div>
        );
        handleSelectCompany(response.data.company);
      }
      
      setShowAddNewDialog(false);
      setNewCompanyData({
        name: '', country: '', city: '', address: '', vat_number: '', email: '', contact_person: '', phone: ''
      });
    } catch (error) {
      console.error('Failed to register company:', error);
      toast.error(error.response?.data?.detail || 'Failed to register company');
    } finally {
      setAddingCompany(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <Label className="text-xs text-slate-500 flex items-center gap-2">
        <Building className="h-3.5 w-3.5" />
        Pantheon Company {required && <span className="text-red-500">*</span>}
      </Label>
      
      {/* Selected Company Display */}
      {selectedCompany ? (
        <div className="mt-1 flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex-1 flex items-center gap-3">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
              selectedCompany.type === 'group' ? 'bg-indigo-100' : 'bg-green-100'
            }`}>
              {selectedCompany.type === 'group' ? (
                <Users className="h-4 w-4 text-indigo-600" />
              ) : (
                <Building className="h-4 w-4 text-green-600" />
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-900 text-sm">{selectedCompany.name}</p>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                {selectedCompany.country && <span>{selectedCompany.country}</span>}
                {selectedCompany.accounting_ref && (
                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                    Pantheon: {selectedCompany.accounting_ref}
                  </span>
                )}
                {selectedCompany.pantheon_synced === false && (
                  <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Pending Pantheon Sync
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClearSelection}
            className="text-slate-400 hover:text-slate-600 h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        /* Search Input */
        <div className="relative mt-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            ref={inputRef}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder={placeholder}
            className="pl-10 pr-10"
            data-testid="pantheon-company-search"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
          )}
        </div>
      )}

      {/* Dropdown */}
      {showDropdown && !selectedCompany && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-80 overflow-hidden">
          {/* Search Results */}
          {searchQuery.length >= 2 ? (
            <div className="max-h-64 overflow-y-auto">
              {/* Groups */}
              {showGroupOption && groups.length > 0 && (
                <>
                  <div className="px-3 py-2 text-xs font-medium text-indigo-600 bg-indigo-50 border-b sticky top-0">
                    Company Groups
                  </div>
                  {groups.slice(0, 5).map((group) => (
                    <button
                      key={`group-${group.id}`}
                      type="button"
                      onClick={() => handleSelectCompany({ ...group, type: 'group' })}
                      className="w-full px-3 py-2 text-left hover:bg-indigo-50 flex items-center gap-3 transition-colors"
                    >
                      <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: group.color || '#6366f1' }}>
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{group.name}</p>
                        <p className="text-xs text-slate-500">{group.company_count || 0} companies</p>
                      </div>
                    </button>
                  ))}
                </>
              )}

              {/* Pantheon Companies */}
              {companies.length > 0 && (
                <>
                  <div className="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-50 border-b sticky top-0">
                    Pantheon Companies ({companies.length})
                  </div>
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => handleSelectCompany(company)}
                      className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors"
                    >
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <Building className="h-4 w-4 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{company.name}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-2">
                          {company.country && <span>{company.country}</span>}
                          {company.accounting_ref && (
                            <span className="text-amber-600">Acc: {company.accounting_ref}</span>
                          )}
                          {company.vat_number && (
                            <span className="text-slate-400">VAT: {company.vat_number}</span>
                          )}
                        </p>
                      </div>
                      {company.pantheon_synced && (
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </>
              )}

              {/* No Results */}
              {companies.length === 0 && groups.length === 0 && !loading && (
                <div className="p-4 text-center">
                  <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 mb-3">No company found for "{searchQuery}"</p>
                  <Button
                    type="button"
                    onClick={() => {
                      setNewCompanyData(prev => ({ ...prev, name: searchQuery }));
                      setShowAddNewDialog(true);
                      setShowDropdown(false);
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    size="sm"
                    data-testid="add-new-company-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Register New Company
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Instruction */
            <div className="p-4 text-center text-sm text-slate-500">
              <Search className="h-6 w-6 text-slate-300 mx-auto mb-2" />
              Type at least 2 characters to search Pantheon companies
            </div>
          )}

          {/* Add New Company Button (always visible when typing) */}
          {searchQuery.length >= 2 && companies.length > 0 && (
            <div className="p-2 border-t bg-slate-50">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setNewCompanyData(prev => ({ ...prev, name: searchQuery }));
                  setShowAddNewDialog(true);
                  setShowDropdown(false);
                }}
                className="w-full text-emerald-600 border-emerald-300 hover:bg-emerald-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Can't find it? Register "{searchQuery}" as new company
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Add New Company Dialog */}
      <Dialog open={showAddNewDialog} onOpenChange={setShowAddNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-600" />
              Register New Company
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
              <Mail className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Company will be added to Pantheon</p>
                <p className="text-amber-700">An email will be sent to <strong>acc@europacific.com</strong> to create this company in the Pantheon accounting system.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Company Name *</Label>
                <Input
                  value={newCompanyData.name}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="ABC Trading Co."
                  className="mt-1"
                  data-testid="new-company-name"
                />
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Country</Label>
                <Input
                  value={newCompanyData.country}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="Slovenia"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">City</Label>
                <Input
                  value={newCompanyData.city}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Ljubljana"
                  className="mt-1"
                />
              </div>
              
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Address</Label>
                <Input
                  value={newCompanyData.address}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Business Street"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">VAT Number</Label>
                <Input
                  value={newCompanyData.vat_number}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, vat_number: e.target.value }))}
                  placeholder="SI12345678"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Email</Label>
                <Input
                  type="email"
                  value={newCompanyData.email}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="info@company.com"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Contact Person</Label>
                <Input
                  value={newCompanyData.contact_person}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, contact_person: e.target.value }))}
                  placeholder="John Smith"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Phone</Label>
                <Input
                  value={newCompanyData.phone}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+386 1 234 5678"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddNewDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddNewCompany}
                disabled={addingCompany || !newCompanyData.name.trim()}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-new-company-btn"
              >
                {addingCompany ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Register Company
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PantheonCompanySelector;
